import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ALL_MODULES = [
  { id: 'messages', label: 'Kontakt', icon: '✉️' },
  { id: 'offers', label: 'Oferty', icon: '🏪' },
  { id: 'issues', label: 'Issues / Odrzucone', icon: '⚖️' },
  { id: 'aiopen', label: 'AI OPEN', icon: '🤖' },
  { id: 'settings', label: 'Ustawienia', icon: '⚙️' },
];

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  active: boolean;
  modules: string[];
  created_at: string;
}

interface Props {
  onBack: () => void;
  token: string;
  currentUser?: any;
}

export default function Users({ onBack, token, currentUser = null }: Props) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editModules, setEditModules] = useState<string[]>([]);
  const [editRole, setEditRole] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editName, setEditName] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('agent');
  const [newModules, setNewModules] = useState<string[]>(['issues', 'messages', 'aiopen', 'offers', 'settings']);
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchUsers = async () => {
    try {
      const res = await axios.get('/auth/users', { headers });
      setUsers(res.data);
    } catch {
      setError('Błąd pobierania użytkowników');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchUsers(); }, []);

  const startEdit = (u: User) => {
    setEditId(u.id);
    setEditModules([...u.modules]);
    setEditRole(u.role);
    setEditActive(u.active);
    setEditName(u.name);
    setEditPassword('');
  };

  const saveEdit = async () => {
    if (!editId) return;
    setSaving(true);
    try {
      const body: any = { name: editName, role: editRole, active: editActive, modules: editModules };
      if (editPassword) body.password = editPassword;
      await axios.patch(`/auth/users/${editId}`, body, { headers });
      setEditId(null);
      fetchUsers();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Błąd zapisu');
    } finally {
      setSaving(false);
    }
  };

  const deleteUser = async (id: string, name: string) => {
    if (!window.confirm(`Usunąć użytkownika ${name}?`)) return;
    try {
      await axios.delete(`/auth/users/${id}`, { headers });
      fetchUsers();
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Błąd usuwania');
    }
  };

  const addUser = async () => {
    setAddError('');
    if (!newName || !newEmail || !newPassword) { setAddError('Wypełnij wszystkie pola'); return; }
    setAdding(true);
    try {
      await axios.post('/auth/users', { name: newName, email: newEmail, password: newPassword, role: newRole, modules: newModules }, { headers });
      setShowAdd(false);
      setNewName(''); setNewEmail(''); setNewPassword(''); setNewRole('agent');
      setNewModules(['issues', 'messages', 'aiopen', 'offers', 'settings']);
      fetchUsers();
    } catch (e: any) {
      setAddError(e.response?.data?.detail || 'Błąd dodawania');
    } finally {
      setAdding(false);
    }
  };

  const toggleModule = (list: string[], mod: string) =>
    list.includes(mod) ? list.filter(m => m !== mod) : [...list, mod];

  const s = {
    page: { minHeight: '100vh', background: '#f8fafc', fontFamily: 'system-ui, sans-serif' } as any,
    header: { background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 12 } as any,
    back: { background: 'none', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', fontSize: 13 } as any,
    content: { maxWidth: 800, margin: '0 auto', padding: '24px 16px' } as any,
    card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, marginBottom: 12, overflow: 'hidden' } as any,
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', gap: 12 } as any,
    name: { fontWeight: 600, fontSize: 14, color: '#111827' } as any,
    email: { fontSize: 12, color: '#6b7280', marginTop: 2 } as any,
    badge: (color: string, bg: string) => ({ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: bg, color, marginRight: 4 }) as any,
    modChip: (active: boolean) => ({ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: active ? '#eff6ff' : '#f3f4f6', color: active ? '#1d4ed8' : '#9ca3af', border: `1px solid ${active ? '#bfdbfe' : '#e5e7eb'}`, marginRight: 4, marginBottom: 4, cursor: 'pointer', display: 'inline-block' }) as any,
    editBox: { background: '#f8fafc', borderTop: '1px solid #e5e7eb', padding: '16px 18px' } as any,
    input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', fontSize: 13, width: '100%', marginBottom: 8 } as any,
    btn: (color: string, bg: string) => ({ background: bg, color, border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 500 }) as any,
    label: { fontSize: 12, color: '#6b7280', marginBottom: 4, display: 'block' } as any,
  };

  return (
    <>
    <div style={s.page}>
      <div style={s.header}>
        <button style={s.back} onClick={onBack}>← Wstecz</button>
        <span style={{ fontWeight: 600, fontSize: 16 }}>👥 Użytkownicy</span>
      </div>
      <div style={s.content}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button style={s.btn('#fff', '#1d4ed8')} onClick={() => setShowAdd(!showAdd)}>+ Dodaj użytkownika</button>
        </div>

        {showAdd && (
          <div style={{ ...s.card, border: '1px solid #bfdbfe' }}>
            <div style={{ padding: '16px 18px' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12 }}>Nowy użytkownik</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <div><label style={s.label}>Imię i nazwisko</label><input style={s.input} value={newName} onChange={e => setNewName(e.target.value)} placeholder="Jan Kowalski" /></div>
                <div><label style={s.label}>Email</label><input style={s.input} value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="jan@firma.pl" /></div>
                <div><label style={s.label}>Hasło</label><input style={s.input} type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="min. 8 znaków" /></div>
                <div><label style={s.label}>Rola</label>
                  <select style={s.input} value={newRole} onChange={e => setNewRole(e.target.value)}>
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <label style={{ ...s.label, marginTop: 8 }}>Dostęp do modułów</label>
              <div style={{ marginBottom: 12 }}>
                {ALL_MODULES.map(m => (
                  <span key={m.id} style={s.modChip(newModules.includes(m.id))} onClick={() => setNewModules(toggleModule(newModules, m.id))}>
                    {m.icon} {m.label}
                  </span>
                ))}
              </div>
              {addError && <div style={{ color: '#dc2626', fontSize: 12, marginBottom: 8 }}>{addError}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={s.btn('#fff', '#1d4ed8')} onClick={addUser} disabled={adding}>{adding ? 'Zapisywanie...' : 'Dodaj'}</button>
                <button style={s.btn('#374151', '#f3f4f6')} onClick={() => setShowAdd(false)}>Anuluj</button>
              </div>
            </div>
          </div>
        )}

        {loading && <div style={{ textAlign: 'center', color: '#6b7280', padding: 40 }}>Ładowanie...</div>}
        {error && <div style={{ color: '#dc2626', padding: 16 }}>{error}</div>}

        {users.map(u => (
          <div key={u.id} style={s.card}>
            <div style={s.row}>
              <div style={{ flex: 1 }}>
                <div style={s.name}>{u.name}</div>
                <div style={s.email}>{u.email}</div>
                <div style={{ marginTop: 6 }}>
                  <span style={s.badge(u.role === 'admin' ? '#7c3aed' : '#1d4ed8', u.role === 'admin' ? '#faf5ff' : '#eff6ff')}>{u.role}</span>
                  <span style={s.badge(u.active ? '#15803d' : '#dc2626', u.active ? '#f0fdf4' : '#fef2f2')}>{u.active ? 'aktywny' : 'nieaktywny'}</span>
                </div>
                <div style={{ marginTop: 6 }}>
                  {ALL_MODULES.map(m => (
                    <span key={m.id} style={{ ...s.modChip(u.modules?.includes(m.id)), cursor: 'default' }}>{m.icon} {m.label}</span>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button style={s.btn('#374151', '#f3f4f6')} onClick={() => editId === u.id ? setEditId(null) : startEdit(u)}>
                  {editId === u.id ? 'Zamknij' : 'Edytuj'}
                </button>
                <button style={s.btn('#dc2626', '#fef2f2')} onClick={() => deleteUser(u.id, u.name)}>Usuń</button>
              </div>
            </div>

            {editId === u.id && (
              <div style={s.editBox}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <div><label style={s.label}>Imię i nazwisko</label><input style={s.input} value={editName} onChange={e => setEditName(e.target.value)} /></div>
                  <div><label style={s.label}>Nowe hasło (opcjonalnie)</label><input style={s.input} type="password" value={editPassword} onChange={e => setEditPassword(e.target.value)} placeholder="zostaw puste = bez zmian" /></div>
                  <div><label style={s.label}>Rola</label>
                    <select style={s.input} value={editRole} onChange={e => setEditRole(e.target.value)}>
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div><label style={s.label}>Status</label>
                    <select style={s.input} value={editActive ? 'true' : 'false'} onChange={e => setEditActive(e.target.value === 'true')}>
                      <option value="true">Aktywny</option>
                      <option value="false">Nieaktywny</option>
                    </select>
                  </div>
                </div>
                <label style={s.label}>Dostęp do modułów</label>
                <div style={{ marginBottom: 12 }}>
                  {ALL_MODULES.map(m => (
                    <span key={m.id} style={s.modChip(editModules.includes(m.id))} onClick={() => setEditModules(toggleModule(editModules, m.id))}>
                      {m.icon} {m.label}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={s.btn('#fff', '#1d4ed8')} onClick={saveEdit} disabled={saving}>{saving ? 'Zapisywanie...' : 'Zapisz zmiany'}</button>
                  <button style={s.btn('#374151', '#f3f4f6')} onClick={() => setEditId(null)}>Anuluj</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
    </>
  );
}
